/**
 * street_furniture.ts — the CIVIC FURNISHING PLANNER (pure, deterministic).
 *
 * Replaces the thinned engine scatter with real urban-design rules:
 *  - The FURNISHING ZONE (NACTO/CityEngine): furniture lives in the curbside
 *    band between the carriageway and the walking through-zone — offset
 *    halfWidth + ~35% of the sidewalk, never on the road, never at the door.
 *  - LAMPS at class-tuned spacing (S/H ≈ 3): arterials both sides STAGGERED,
 *    collectors alternating, locals single-side; 8 m intersection keep-out
 *    (the corner clear zone).
 *  - SEATING where people are (Whyte): a center-facing ring at the plaza's
 *    edge, conversation PAIRS on the plaza, block-corner benches facing the
 *    street on arterials, a waste unit beside every second bench.
 *  - CHARACTER props: bollard clusters guarding arterial corners, planters
 *    mid-span between lamps, kiosks/vending at corner clusters near the core,
 *    banner poles as a gateway pair on the plaza approach, market stalls +
 *    crates on the market pad's frontage, bike racks at civic/market doors,
 *    one water feature off the plaza center (the EVENT stage stays clear).
 *
 *  Every placement is VALIDATED: outside every building pad polygon (with a
 *  margin for render-time overhang), off every road surface, clear of the
 *  town's venue slots + notice board + mission terminal, min-spaced from its
 *  neighbors, and slope-checked. The renderer re-validates against the
 *  ACTUAL scaled footprints it placed (fit-scale can outgrow the pad).
 */
import type { CityPlan, RoadEdge } from 'enginex/city/config.js';
import type { SettlementSite } from '../../../shared/settlements.js';
import { townSlots } from '../../../shared/town_layout.js';

/** CommonsSettlementsModule's avalanche hash (numeric a,b) — kept identical
 *  so planner rolls and module rolls share one deterministic universe. */
function h01(a: number, b: number): number {
  let s = (Math.imul(a >>> 0, 0x9e3779b1) ^ Math.imul((b >>> 0) + 1, 0x85ebca77)) >>> 0;
  s = Math.imul(s ^ (s >>> 15), 0x2c1b3c6d) >>> 0;
  s = Math.imul(s ^ (s >>> 13), 0x297a2d39) >>> 0;
  return ((s ^ (s >>> 15)) >>> 0) / 4294967296;
}

export type FurnKind =
  | 'lamp' | 'bench' | 'waste' | 'planter' | 'bollard' | 'kiosk' | 'banner'
  | 'stall' | 'crate' | 'bikerack' | 'water' | 'signpost';

export interface FurniturePlacement {
  key: string;
  /** LOCAL plan coords (add site.centerX/Z for world). */
  x: number;
  z: number;
  yaw: number;
  kind: FurnKind;
  /** Real-world target height (m) — the renderer scales the GLB to this. */
  targetH: number;
  /** Solid enough to deserve a walk collider. */
  solid: boolean;
}

/** Real-world silhouettes per cooked prop key (fit-scale targets, metres). */
export const FURN_TARGET_H: Record<string, number> = {
  'prop:solar_lamp': 5.0,
  'prop:street_lamp_curved': 5.6,
  'prop:energy_bollard': 1.1,
  'prop:antigrav_bench': 0.95,
  'prop:seating_pod': 1.25,
  'prop:trash_recycler': 1.35,
  'prop:planter_large': 1.15,
  'prop:hydroponic_planter': 1.5,
  'prop:bollard_cluster': 1.0,
  'prop:info_kiosk': 2.6,
  'prop:vending_unit': 2.2,
  'prop:holo_signpost': 3.4,
  'prop:banner_pole': 6.0,
  'prop:market_stall': 2.9,
  'prop:supply_crate': 1.1,
  'prop:cargo_pallet': 0.5,
  'prop:bike_rack_antigrav': 1.05,
  'prop:water_feature_small': 1.7,
  'prop:comms_relay_small': 2.4,
};

// ── Rules (research-tuned) ──────────────────────────────────────────────────
const LAMP_SPACING: Record<RoadEdge['roadClass'], number> = { arterial: 26, collector: 30, local: 34 };
const INTERSECTION_KEEPOUT = 8;
const BENCH_SPACING_ARTERIAL = 48;
const PLANTER_EVERY = 2;          // a planter mid-span between every other lamp pair
const FURN_MIN_GAP = 1.6;         // furniture-to-furniture clearance (grid-checked)
const PAD_MARGIN = 2.5;           // clearance from a pad polygon edge (render overhang)
const ROAD_MARGIN = 0.4;          // clearance beyond a carriageway edge
const VENUE_CLEAR = 6;            // event stages / podiums stay clear
const BOARD_CLEAR = 5.5;          // the civic notice board spot (plaza +6,+4)
const PLAZA_EVENT_CLEAR = 14;     // the plaza CENTER is the ceremony stage
const MAX_SLOPE = 0.35;

/** Per-tier budgets (planner is spacing-bounded; these are hard guards). */
const BUDGET: Record<string, Record<FurnKind, number>> = {
  city: { lamp: 150, bench: 44, waste: 22, planter: 30, bollard: 24, kiosk: 10, banner: 8, stall: 10, crate: 12, bikerack: 10, water: 1, signpost: 8 },
  town: { lamp: 52, bench: 16, waste: 8, planter: 12, bollard: 10, kiosk: 4, banner: 4, stall: 6, crate: 8, bikerack: 5, water: 1, signpost: 4 },
  hamlet: { lamp: 14, bench: 6, waste: 3, planter: 4, bollard: 4, kiosk: 1, banner: 2, stall: 3, crate: 4, bikerack: 2, water: 0, signpost: 2 },
  // Capital wave (2026-09-15): a district IS a city-scale plan; the campus plaza a
  // town-scale one; a capital's plan is empty until phase 2. A missing key here
  // threw `reading 'lamp'` inside the district build and no district ever assembled.
  district: { lamp: 150, bench: 44, waste: 22, planter: 30, bollard: 24, kiosk: 10, banner: 8, stall: 10, crate: 12, bikerack: 10, water: 1, signpost: 8 },
  capital: { lamp: 150, bench: 44, waste: 22, planter: 30, bollard: 24, kiosk: 10, banner: 8, stall: 10, crate: 12, bikerack: 10, water: 1, signpost: 8 },
  circuit: { lamp: 52, bench: 16, waste: 8, planter: 12, bollard: 10, kiosk: 4, banner: 4, stall: 6, crate: 8, bikerack: 5, water: 1, signpost: 4 },
};

// ── Geometry helpers (pure) ─────────────────────────────────────────────────
function distToPolyline(px: number, pz: number, poly: Float64Array): number {
  let best = Infinity;
  for (let i = 0; i + 3 < poly.length; i += 2) {
    const ax = poly[i], az = poly[i + 1], bx = poly[i + 2], bz = poly[i + 3];
    const dx = bx - ax, dz = bz - az;
    const L2 = dx * dx + dz * dz;
    const t = L2 > 0 ? Math.max(0, Math.min(1, ((px - ax) * dx + (pz - az) * dz) / L2)) : 0;
    const qx = ax + dx * t, qz = az + dz * t;
    best = Math.min(best, Math.hypot(px - qx, pz - qz));
  }
  return best;
}

/** Signed distance to a closed polygon (negative = inside). */
function signedDistToPolygon(px: number, pz: number, poly: Float64Array): number {
  let inside = false;
  let best = Infinity;
  const n = poly.length / 2;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = poly[i * 2], zi = poly[i * 2 + 1];
    const xj = poly[j * 2], zj = poly[j * 2 + 1];
    const dx = xj - xi, dz = zj - zi;
    const L2 = dx * dx + dz * dz;
    const t = L2 > 0 ? Math.max(0, Math.min(1, ((px - xi) * dx + (pz - zi) * dz) / L2)) : 0;
    best = Math.min(best, Math.hypot(px - (xi + dx * t), pz - (zi + dz * t)));
    if ((zi > pz) !== (zj > pz) && px < ((xj - xi) * (pz - zi)) / (zj - zi + 1e-12) + xi) inside = !inside;
  }
  return inside ? -best : best;
}

interface Ctx {
  plan: CityPlan;
  tier: SettlementSite['tier'];
  seed: number;
  out: FurniturePlacement[];
  counts: Map<FurnKind, number>;
  grid: Map<string, Array<{ x: number; z: number }>>;
  keepClear: Array<{ x: number; z: number; r: number }>;
  slope: (x: number, z: number) => number;
  padGrid: Map<string, number[]>;
  edgeGrid: Map<string, number[]>;
}

const CELL = 24;
const gkey = (x: number, z: number): string => `${Math.floor(x / CELL)},${Math.floor(z / CELL)}`;

function nearCells(x: number, z: number): string[] {
  const cx = Math.floor(x / CELL), cz = Math.floor(z / CELL);
  const out: string[] = [];
  for (let a = -1; a <= 1; a++) for (let b = -1; b <= 1; b++) out.push(`${cx + a},${cz + b}`);
  return out;
}

/** The full validation gate — the heart of "properly placed". */
function fits(ctx: Ctx, x: number, z: number, minGap = FURN_MIN_GAP): boolean {
  // Buildings: outside every nearby pad polygon by the overhang margin.
  for (const c of nearCells(x, z)) {
    for (const pi of ctx.padGrid.get(c) ?? []) {
      if (signedDistToPolygon(x, z, ctx.plan.buildings[pi].footprint) < PAD_MARGIN) return false;
    }
    // Roads: never on any carriageway (corners cross other edges).
    for (const ei of ctx.edgeGrid.get(c) ?? []) {
      const e = ctx.plan.roads.edges[ei];
      if (distToPolyline(x, z, e.polyline) < e.halfWidth + ROAD_MARGIN) return false;
    }
    // Furniture spacing.
    for (const p of ctx.grid.get(c) ?? []) {
      if (Math.hypot(p.x - x, p.z - z) < minGap) return false;
    }
  }
  // Civic keep-clears (venue stages, notice board, terminal, plaza center).
  for (const k of ctx.keepClear) {
    if (Math.hypot(k.x - x, k.z - z) < k.r) return false;
  }
  if (ctx.slope(x, z) > MAX_SLOPE) return false;
  return true;
}

function put(ctx: Ctx, kind: FurnKind, key: string | null, x: number, z: number, yaw: number, solid: boolean, minGap = FURN_MIN_GAP): boolean {
  if (!key) return false;
  const budget = BUDGET[ctx.tier][kind];
  if ((ctx.counts.get(kind) ?? 0) >= budget) return false;
  if (!fits(ctx, x, z, minGap)) return false;
  const targetH = FURN_TARGET_H[key] ?? 1.2;
  ctx.out.push({ key, x, z, yaw, kind, targetH, solid });
  ctx.counts.set(kind, (ctx.counts.get(kind) ?? 0) + 1);
  const cell = gkey(x, z);
  let arr = ctx.grid.get(cell);
  if (!arr) { arr = []; ctx.grid.set(cell, arr); }
  arr.push({ x, z });
  return true;
}

const pick = (arr: readonly string[], h: number): string => arr[Math.floor(h * arr.length) % arr.length];
const LAMPS = ['prop:street_lamp_curved', 'prop:solar_lamp'] as const;
const SEATS = ['prop:antigrav_bench', 'prop:seating_pod'] as const;
const PLANTERS = ['prop:planter_large', 'prop:hydroponic_planter'] as const;
const KIOSKS = ['prop:info_kiosk', 'prop:vending_unit'] as const;

/** Arc-length walk over an edge polyline calling fn at every `step` metres. */
function walkEdge(e: RoadEdge, step: number, phase: number, fn: (x: number, z: number, dirX: number, dirZ: number, s: number, total: number) => void): void {
  const p = e.polyline;
  let total = 0;
  for (let i = 0; i + 3 < p.length; i += 2) total += Math.hypot(p[i + 2] - p[i], p[i + 3] - p[i + 1]);
  if (total < step) return;
  let next = phase * step;
  let acc = 0;
  for (let i = 0; i + 3 < p.length; i += 2) {
    const ax = p[i], az = p[i + 1], bx = p[i + 2], bz = p[i + 3];
    const segL = Math.hypot(bx - ax, bz - az);
    if (segL < 1e-6) continue;
    const dx = (bx - ax) / segL, dz = (bz - az) / segL;
    while (next <= acc + segL) {
      const t = (next - acc) / segL;
      fn(ax + (bx - ax) * t, az + (bz - az) * t, dx, dz, next, total);
      next += step;
    }
    acc += segL;
  }
}

/**
 * The planner. Local plan coords in, deterministic, render-agnostic.
 * `slopeAt` receives LOCAL coords (the caller closes over world terrain).
 */
export function planStreetFurniture(
  plan: CityPlan, site: SettlementSite, seed: string,
  slopeAt: (lx: number, lz: number) => number,
): FurniturePlacement[] {
  const s = site.siteSeed;
  const ctx: Ctx = {
    plan, tier: site.tier, seed: s, out: [], counts: new Map(), grid: new Map(),
    keepClear: [], slope: slopeAt, padGrid: new Map(), edgeGrid: new Map(),
  };
  // Spatial grids over pads + edges (bbox superset).
  plan.buildings.forEach((pad, pi) => {
    let minX = Infinity, minZ = Infinity, maxX = -Infinity, maxZ = -Infinity;
    for (let i = 0; i + 1 < pad.footprint.length; i += 2) {
      minX = Math.min(minX, pad.footprint[i]); maxX = Math.max(maxX, pad.footprint[i]);
      minZ = Math.min(minZ, pad.footprint[i + 1]); maxZ = Math.max(maxZ, pad.footprint[i + 1]);
    }
    for (let cx = Math.floor((minX - CELL) / CELL); cx <= Math.floor((maxX + CELL) / CELL); cx++) {
      for (let cz = Math.floor((minZ - CELL) / CELL); cz <= Math.floor((maxZ + CELL) / CELL); cz++) {
        const k = `${cx},${cz}`;
        let a = ctx.padGrid.get(k);
        if (!a) { a = []; ctx.padGrid.set(k, a); }
        a.push(pi);
      }
    }
  });
  plan.roads.edges.forEach((e, ei) => {
    // RASTERIZE along segments (vertex-only registration left mid-segment
    // cells blind — furniture snuck onto long straight carriageways).
    const p = e.polyline;
    const step = CELL / 2;
    for (let i = 0; i + 3 < p.length; i += 2) {
      const ax = p[i], az = p[i + 1], bx = p[i + 2], bz = p[i + 3];
      const L = Math.hypot(bx - ax, bz - az);
      const n = Math.max(1, Math.ceil(L / step));
      for (let k2 = 0; k2 <= n; k2++) {
        const t = k2 / n;
        for (const k of nearCells(ax + (bx - ax) * t, az + (bz - az) * t)) {
          let a = ctx.edgeGrid.get(k);
          if (!a) { a = []; ctx.edgeGrid.set(k, a); }
          if (a[a.length - 1] !== ei) a.push(ei);
        }
      }
    }
  });
  // Civic keep-clears: every venue slot (event stages), the notice board, the plaza heart.
  try {
    for (const slot of townSlots(seed, site)) {
      const lx = slot.x - site.centerX, lz = slot.z - site.centerZ;
      ctx.keepClear.push({ x: lx, z: lz, r: VENUE_CLEAR });
      if (slot.key === 'plaza') {
        ctx.keepClear.push({ x: lx, z: lz, r: PLAZA_EVENT_CLEAR });
        ctx.keepClear.push({ x: lx + 6, z: lz + 4, r: BOARD_CLEAR }); // the notice board
      }
    }
  } catch { /* site-less worlds: no slots */ }

  // Intersection map: node degree ≥ 3 = a corner cluster candidate.
  const nodeDeg = new Map<number, number>();
  for (const e of plan.roads.edges) {
    nodeDeg.set(e.from, (nodeDeg.get(e.from) ?? 0) + 1);
    nodeDeg.set(e.to, (nodeDeg.get(e.to) ?? 0) + 1);
  }
  const nodeXZ = (id: number): { x: number; z: number } | null => {
    const n = plan.roads.nodes[id] as unknown as { x: number; z: number } | undefined;
    return n ? { x: n.x, z: n.z } : null;
  };
  const nearNode = (x: number, z: number): boolean => {
    for (const e of plan.roads.edges) {
      for (const id of [e.from, e.to]) {
        const n = nodeXZ(id);
        if (n && Math.hypot(n.x - x, n.z - z) < INTERSECTION_KEEPOUT) return true;
      }
    }
    return false;
  };

  const sidewalk = plan.config.roads.sidewalkWidth;
  const furnOff = (hw: number): number => hw + Math.max(0.7, sidewalk * 0.35);

  // ── 1. LAMPS: class spacing; arterials both sides staggered, collectors
  //    alternating, locals single-side. ────────────────────────────────────
  plan.roads.edges.forEach((e, ei) => {
    const spacing = LAMP_SPACING[e.roadClass];
    const off = furnOff(e.halfWidth);
    const phase = 0.35 + h01(s, 31 + ei) * 0.3;
    let n = 0;
    walkEdge(e, spacing, phase, (x, z, dx, dz) => {
      if (nearNode(x, z)) return;
      const lampKey = pick(LAMPS, h01(s, 101 + ei));
      const yawFace = Math.atan2(dx, dz) + Math.PI / 2; // face the street
      if (e.roadClass === 'arterial') {
        // Both sides, staggered by half a spacing (the second side offset).
        const side = n % 2 === 0 ? 1 : -1;
        put(ctx, 'lamp', lampKey, x + -dz * off * side, z + dx * off * side, yawFace, false);
      } else if (e.roadClass === 'collector') {
        const side = n % 2 === 0 ? 1 : -1;
        put(ctx, 'lamp', lampKey, x + -dz * off * side, z + dx * off * side, yawFace, false);
      } else {
        const side = ei % 2 === 0 ? 1 : -1; // one consistent side per lane
        put(ctx, 'lamp', lampKey, x + -dz * off * side, z + dx * off * side, yawFace, false);
      }
      // Planter mid-span between every other lamp pair on arterials.
      if (e.roadClass === 'arterial' && n % PLANTER_EVERY === 0) {
        const mx = x + dx * spacing * 0.5, mz = z + dz * spacing * 0.5;
        const side = n % 2 === 0 ? 1 : -1;
        put(ctx, 'planter', pick(PLANTERS, h01(s, 211 + ei + n)), mx + -dz * off * side, mz + dx * off * side, Math.atan2(dx, dz), true);
      }
      n++;
    });
  });

  // ── 2. The PLAZA (Sitte's room, Whyte's edges): center-facing seat ring,
  //    conversation pairs, banner gateway, the one water feature. ──────────
  let plaza: { cx: number; cz: number; r: number } | null = null;
  for (const pc of plan.parcels) {
    if (pc.zone !== 'park') continue;
    let pcx = 0, pcz = 0;
    const n = pc.polygon.length / 2;
    for (let i = 0; i < n; i++) { pcx += pc.polygon[i * 2] / n; pcz += pc.polygon[i * 2 + 1] / n; }
    if (plaza && Math.hypot(pcx, pcz) >= Math.hypot(plaza.cx, plaza.cz)) continue;
    let pr = 0;
    for (let i = 0; i < n; i++) pr = Math.max(pr, Math.hypot(pc.polygon[i * 2] - pcx, pc.polygon[i * 2 + 1] - pcz));
    plaza = { cx: pcx, cz: pcz, r: Math.min(46, Math.max(12, pr * 0.82)) };
  }
  // Park-less archetypes (the dense lattice has NO park zone): the plaza is
  // still real — the town-layout 'plaza' venue slot anchors it at the core.
  if (!plaza) {
    try {
      for (const slot of townSlots(seed, site)) {
        if (slot.key !== 'plaza') continue;
        plaza = {
          cx: slot.x - site.centerX, cz: slot.z - site.centerZ,
          r: PLAZA_EVENT_CLEAR + (site.tier === 'city' ? 9 : site.tier === 'town' ? 6 : 3),
        };
        break;
      }
    } catch { /* site-less worlds */ }
  }
  if (plaza) {
    const ringN = site.tier === 'city' ? 16 : site.tier === 'town' ? 10 : 6;
    for (let i = 0; i < ringN; i++) {
      const ang = (i / ringN) * Math.PI * 2 + h01(s, 9103) * 0.6;
      const x = plaza.cx + Math.cos(ang) * plaza.r;
      const z = plaza.cz + Math.sin(ang) * plaza.r;
      const yawCenter = Math.atan2(plaza.cx - x, plaza.cz - z); // face the room
      if (i % 3 === 2) put(ctx, 'lamp', pick(LAMPS, h01(s, 9101)), x, z, yawCenter, false);
      else {
        const seated = put(ctx, 'bench', pick(SEATS, h01(s, 9102 + i)), x, z, yawCenter, true);
        // Whyte: a waste unit beside every second seat keeps the edge honest.
        if (seated && i % 2 === 0) {
          put(ctx, 'waste', 'prop:trash_recycler', x + Math.cos(ang + Math.PI / 2) * 1.9, z + Math.sin(ang + Math.PI / 2) * 1.9, yawCenter, true);
        }
      }
    }
    // Conversation pairs INSIDE the ring (face to face), off the event heart.
    const pairs = site.tier === 'city' ? 3 : site.tier === 'town' ? 2 : 1;
    for (let i = 0; i < pairs; i++) {
      const ang = h01(s, 9200 + i) * Math.PI * 2;
      const rr = PLAZA_EVENT_CLEAR + 3 + h01(s, 9210 + i) * Math.max(2, plaza.r - PLAZA_EVENT_CLEAR - 5);
      const x = plaza.cx + Math.cos(ang) * rr, z = plaza.cz + Math.sin(ang) * rr;
      const fx = Math.cos(ang + Math.PI / 2), fz = Math.sin(ang + Math.PI / 2);
      const a = put(ctx, 'bench', pick(SEATS, h01(s, 9220 + i)), x + fx * 1.3, z + fz * 1.3, Math.atan2(-fx, -fz), true, 1.0);
      if (a) put(ctx, 'bench', pick(SEATS, h01(s, 9230 + i)), x - fx * 1.3, z - fz * 1.3, Math.atan2(fx, fz), true, 1.0);
    }
    // The water feature: off-center, the ceremony heart stays clear.
    const wang = h01(s, 9300) * Math.PI * 2;
    put(ctx, 'water', 'prop:water_feature_small',
      plaza.cx + Math.cos(wang) * (PLAZA_EVENT_CLEAR + 4), plaza.cz + Math.sin(wang) * (PLAZA_EVENT_CLEAR + 4), wang, true);
    // Banner gateway: a pair flanking the plaza approach from the core road.
    const bang = h01(s, 9310) * Math.PI * 2;
    for (const sgn of [-1, 1]) {
      put(ctx, 'banner', 'prop:banner_pole',
        plaza.cx + Math.cos(bang) * (plaza.r + 4) + Math.cos(bang + Math.PI / 2) * 3.2 * sgn,
        plaza.cz + Math.sin(bang) * (plaza.r + 4) + Math.sin(bang + Math.PI / 2) * 3.2 * sgn,
        bang + Math.PI, false);
    }
  }

  // ── 3. CORNERS: bollards guard arterial intersections; kiosk clusters and
  //    signposts sit just past the corner clear zone near the core. ────────
  // A corner's rank is its BEST class across ALL touching edges (first-edge-
  // wins starved the bollards: locals visited most nodes first).
  const RANK: Record<RoadEdge['roadClass'], number> = { arterial: 2, collector: 1, local: 0 };
  const nodeBest = new Map<number, { rank: number; hw: number }>();
  for (const e of plan.roads.edges) {
    for (const id of [e.from, e.to]) {
      const cur = nodeBest.get(id);
      if (!cur || RANK[e.roadClass] > cur.rank) nodeBest.set(id, { rank: RANK[e.roadClass], hw: e.halfWidth });
    }
  }
  const seenNodes = new Set<number>();
  for (const e of plan.roads.edges) {
    for (const id of [e.from, e.to]) {
      if (seenNodes.has(id)) continue;
      seenNodes.add(id);
      const deg = nodeDeg.get(id) ?? 0;
      if (deg < 3) continue;
      const nn = nodeXZ(id);
      if (!nn) continue;
      const best = nodeBest.get(id) ?? { rank: 0, hw: 3 };
      const coreFrac = Math.hypot(nn.x, nn.z) / Math.max(1, plan.config.radius);
      const h = h01(s, 500 + id);
      // Bollard cluster on one corner of bigger intersections — sweep the
      // corner angles: most bearings sit ON a crossing carriageway.
      if (best.rank >= 1 && h < 0.6) {
        const rr = best.hw + INTERSECTION_KEEPOUT * 0.55;
        for (let k = 0; k < 8; k++) {
          const ang = (h + k / 8) * Math.PI * 2;
          if (put(ctx, 'bollard', h < 0.3 ? 'prop:bollard_cluster' : 'prop:energy_bollard',
            nn.x + Math.cos(ang) * rr, nn.z + Math.sin(ang) * rr, ang, false)) break;
        }
      }
      // Kiosks + signposts cluster near the core (corner commerce).
      if (coreFrac < 0.45 && h01(s, 600 + id) < 0.4) {
        const rr = best.hw + INTERSECTION_KEEPOUT + 2;
        for (let k = 0; k < 8; k++) {
          const ang = (h01(s, 610 + id) + k / 8) * Math.PI * 2;
          const kx = nn.x + Math.cos(ang) * rr, kz = nn.z + Math.sin(ang) * rr;
          if (put(ctx, 'kiosk', pick(KIOSKS, h01(s, 620 + id)), kx, kz, Math.atan2(nn.x - kx, nn.z - kz), true)) {
            put(ctx, 'signpost', 'prop:holo_signpost', kx + 2.4, kz + 1.1, Math.atan2(nn.x - kx, nn.z - kz), false, 1.2);
            break;
          }
        }
      }
    }
  }

  // ── 4. ARTERIAL benches: block-corner seating facing the street, a waste
  //    unit with every second one (people-watch the flow — Whyte). ─────────
  let benchTick = 0;
  // Bench streets: arterials, plus collectors when the plan is arterial-poor
  // (the dense lattice concentrates arterials on two axes — benches must
  // follow where people actually walk).
  let arterialLen = 0;
  for (const e of plan.roads.edges) {
    if (e.roadClass === 'arterial') {
      const p = e.polyline;
      for (let i = 0; i + 3 < p.length; i += 2) arterialLen += Math.hypot(p[i + 2] - p[i], p[i + 3] - p[i + 1]);
    }
  }
  const benchCollectors = arterialLen < plan.config.radius * 2.5;
  plan.roads.edges.forEach((e, ei) => {
    if (e.roadClass !== 'arterial' && !(benchCollectors && e.roadClass === 'collector')) return;
    const off = furnOff(e.halfWidth) + 0.6; // one step behind the lamp line
    walkEdge(e, e.roadClass === 'arterial' ? BENCH_SPACING_ARTERIAL : BENCH_SPACING_ARTERIAL + 16, 0.5 + h01(s, 700 + ei) * 0.3, (x, z, dx, dz) => {
      if (nearNode(x, z)) return;
      const side = benchTick % 2 === 0 ? 1 : -1;
      const bx = x + -dz * off * side, bz = z + dx * off * side;
      const yawRoad = Math.atan2(x - bx, z - bz); // face the carriageway
      const seated = put(ctx, 'bench', pick(SEATS, h01(s, 710 + ei + benchTick)), bx, bz, yawRoad, true);
      if (seated && benchTick % 2 === 0) {
        put(ctx, 'waste', 'prop:trash_recycler', bx + dx * 1.9, bz + dz * 1.9, yawRoad, true);
      }
      benchTick++;
    });
  });

  // ── 5a. The BAZAAR: stalls arc around the town-layout market venue (its
  //    6 m stage stays clear) — cities' function pads rarely roll 'market'.
  try {
    for (const slot of townSlots(seed, site)) {
      if (slot.key !== 'market') continue;
      const mx = slot.x - site.centerX, mz = slot.z - site.centerZ;
      const stallN = site.tier === 'city' ? 5 : site.tier === 'town' ? 4 : 2;
      let placedStalls = 0;
      for (let k = 0; k < 14 && placedStalls < stallN; k++) {
        const ang = (h01(s, 950) + k / 14) * Math.PI * 2;
        const rr = VENUE_CLEAR + 1.6 + h01(s, 951 + k) * 1.2;
        const x = mx + Math.cos(ang) * rr, z = mz + Math.sin(ang) * rr;
        if (put(ctx, 'stall', 'prop:market_stall', x, z, Math.atan2(mx - x, mz - z), true, 1.2)) placedStalls++;
      }
      if (placedStalls > 0) {
        for (let k = 0; k < 6; k++) {
          const ang = (h01(s, 960) + k / 6) * Math.PI * 2;
          const rr = VENUE_CLEAR + 4.2;
          if (put(ctx, 'crate', k % 2 === 0 ? 'prop:supply_crate' : 'prop:cargo_pallet',
            mx + Math.cos(ang) * rr, mz + Math.sin(ang) * rr, ang, true, 1.1)) break;
        }
      }
      break;
    }
  } catch { /* site-less worlds */ }

  // ── 5b. FRONTAGE props by building function: stalls + crates at the market,
  //    bike racks at civic/market doors. ───────────────────────────────────
  for (const pad of plan.buildings) {
    const fn = pad.program.function as string | undefined;
    if (!fn) continue;
    const f = pad.footprint;
    const cx = (f[0] + f[2] + f[4] + f[6]) / 4;
    const cz = (f[1] + f[3] + f[5] + f[7]) / 4;
    const frontAng = pad.orientation; // pads orient toward their street
    const fx = Math.sin(frontAng), fz = Math.cos(frontAng);
    const frontD = Math.max(pad.program.width, pad.program.depth) * 0.5 + PAD_MARGIN + 1.2;
    if (fn === 'market') {
      const h = h01(s, 800 + pad.parcelId);
      let stallsHere = 0;
      // Dense rowhouse blocks often leave no legal front spot — sweep the
      // four faces and a few lateral offsets until two stalls seat.
      for (const face of [frontAng, frontAng + Math.PI, frontAng + Math.PI / 2, frontAng - Math.PI / 2]) {
        const gx = Math.sin(face), gz = Math.cos(face);
        for (const lat of [-3.4, 3.4, -1.4, 1.4]) {
          if (stallsHere >= 2) break;
          if (put(ctx, 'stall', 'prop:market_stall',
            cx + gx * frontD + -gz * lat, cz + gz * frontD + gx * lat, face + Math.PI, true)) stallsHere++;
        }
        if (stallsHere >= 2) break;
      }
      if (stallsHere > 0) {
        put(ctx, 'crate', h < 0.5 ? 'prop:supply_crate' : 'prop:cargo_pallet',
          cx + fx * (frontD + 2.2), cz + fz * (frontD + 2.2), h * Math.PI * 2, true, 1.2);
      }
    } else if (fn === 'civic' || fn === 'science' || fn === 'entertainment') {
      if (h01(s, 900 + pad.parcelId) < 0.6) {
        put(ctx, 'bikerack', 'prop:bike_rack_antigrav',
          cx + fx * frontD + -fz * 3.0, cz + fz * frontD + fx * 3.0, frontAng, true);
      }
    }
  }

  return ctx.out;
}
